import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  ContentCopyOutlined as CopyIcon,
  DeleteOutlineOutlined as DeleteIcon,
  DescriptionOutlined as FormIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenIcon,
  PeopleAltOutlined as ResponsesIcon,
  SaveOutlined as SaveIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../../api/api";

const FIELD_TYPES = [
  { value: "short_text", label: "Short answer" },
  { value: "long_text", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "datetime", label: "Date and time" },
  { value: "dropdown", label: "Dropdown" },
  { value: "radio", label: "Multiple choice" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "file", label: "File URL" },
  { value: "heading", label: "Section heading" },
];

const OPTION_FIELD_TYPES = ["dropdown", "radio", "checkbox"];

function emptyForm() {
  return {
    Name: "",
    Description: "",
    Slug: "",
    AccessType: "authenticated",
    Status: "draft",
    Fields: [],
  };
}

function makeClientId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function keyify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function normalizeField(field) {
  const options = Array.isArray(field?.Settings?.options)
    ? field.Settings.options
    : [];

  return {
    ...field,
    clientId: field?.Id ? `field-${field.Id}` : makeClientId(),
    FieldKey: field?.FieldKey || "",
    Label: field?.Label || "",
    FieldType: field?.FieldType || "short_text",
    Placeholder: field?.Placeholder || "",
    HelpText: field?.HelpText || "",
    IsRequired: Boolean(field?.IsRequired),
    OptionsText: options.join(", "),
  };
}

function normalizeForm(form) {
  return {
    ...emptyForm(),
    ...form,
    Fields: Array.isArray(form?.Fields)
      ? form.Fields.map(normalizeField)
      : [],
  };
}

function extractError(error, fallbackMessage) {
  const data = error?.response?.data;
  const validationErrors = data?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors).find(
      (messages) => Array.isArray(messages) && messages.length > 0,
    );

    if (firstError?.[0]) {
      return firstError[0];
    }
  }

  return data?.message || error?.message || fallbackMessage;
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatAnswer(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  if (value && typeof value === "object") {
    return JSON.stringify(value);
  }

  return value === undefined || value === null || value === ""
    ? "-"
    : String(value);
}

function statusColor(status) {
  if (status === "published") {
    return "success";
  }

  if (status === "closed") {
    return "error";
  }

  return "default";
}

function FormBuilder() {
  const currentUser = useSelector((state) => state.user.user) || {};
  const token =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [loadingForms, setLoadingForms] = useState(true);
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const publicUrl = useMemo(() => {
    if (!form.Slug) {
      return "";
    }

    const basePath = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return `${window.location.origin}${basePath}/forms/${form.Slug}`;
  }, [form.Slug]);

  const filteredForms = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return forms;
    }

    return forms.filter((item) =>
      [item.Name, item.Description, item.Slug]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [forms, search]);

  const loadForms = async (preferredFormId = selectedFormId) => {
    try {
      setLoadingForms(true);
      const response = await api.getAdminForms({}, token);
      const loadedForms = Array.isArray(response?.data?.forms)
        ? response.data.forms
        : [];
      setForms(loadedForms);

      if (
        preferredFormId &&
        loadedForms.some(
          (item) => String(item.Id) === String(preferredFormId),
        )
      ) {
        setSelectedFormId(preferredFormId);
      } else if (!selectedFormId && loadedForms[0]) {
        setSelectedFormId(loadedForms[0].Id);
      }
    } catch (loadError) {
      setError(extractError(loadError, "Unable to load forms."));
    } finally {
      setLoadingForms(false);
    }
  };

  useEffect(() => {
    loadForms();
    // The token is stable for the signed-in session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selectedFormId) {
      return undefined;
    }

    let active = true;

    async function loadSelectedForm() {
      try {
        setLoadingForm(true);
        setError("");
        const response = await api.getAdminForm(selectedFormId, token);

        if (active) {
          setForm(normalizeForm(response?.data?.form));
        }
      } catch (loadError) {
        if (active) {
          setError(extractError(loadError, "Unable to load the selected form."));
        }
      } finally {
        if (active) {
          setLoadingForm(false);
        }
      }
    }

    loadSelectedForm();

    return () => {
      active = false;
    };
  }, [selectedFormId, token]);

  useEffect(() => {
    if (activeTab !== 1 || !selectedFormId) {
      return undefined;
    }

    let active = true;

    async function loadSubmissions() {
      try {
        setLoadingSubmissions(true);
        setError("");
        const response = await api.getFormSubmissions(selectedFormId, token);

        if (active) {
          setSubmissions(
            Array.isArray(response?.data?.submissions)
              ? response.data.submissions
              : [],
          );
        }
      } catch (loadError) {
        if (active) {
          setError(extractError(loadError, "Unable to load form responses."));
        }
      } finally {
        if (active) {
          setLoadingSubmissions(false);
        }
      }
    }

    loadSubmissions();

    return () => {
      active = false;
    };
  }, [activeTab, selectedFormId, token]);

  const handleNewForm = () => {
    setSelectedFormId("");
    setForm(emptyForm());
    setSubmissions([]);
    setActiveTab(0);
    setError("");
    setMessage("");
  };

  const updateFormValue = (field, value) => {
    setForm((currentForm) => {
      const nextForm = { ...currentForm, [field]: value };

      if (field === "Name" && !selectedFormId) {
        const oldAutoSlug = slugify(currentForm.Name);
        if (!currentForm.Slug || currentForm.Slug === oldAutoSlug) {
          nextForm.Slug = slugify(value);
        }
      }

      return nextForm;
    });
  };

  const addField = () => {
    setForm((currentForm) => {
      const fieldNumber = currentForm.Fields.length + 1;
      return {
        ...currentForm,
        Fields: [
          ...currentForm.Fields,
          normalizeField({
            FieldKey: `field_${fieldNumber}`,
            Label: `Question ${fieldNumber}`,
            FieldType: "short_text",
            IsRequired: false,
          }),
        ],
      };
    });
  };

  const updateField = (clientId, property, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      Fields: currentForm.Fields.map((field) => {
        if (field.clientId !== clientId) {
          return field;
        }

        const nextField = { ...field, [property]: value };
        if (property === "Label") {
          const previousAutoKey = keyify(field.Label);
          if (!field.FieldKey || field.FieldKey === previousAutoKey) {
            nextField.FieldKey = keyify(value);
          }
        }

        return nextField;
      }),
    }));
  };

  const removeField = (clientId) => {
    setForm((currentForm) => ({
      ...currentForm,
      Fields: currentForm.Fields.filter(
        (field) => field.clientId !== clientId,
      ),
    }));
  };

  const moveField = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= form.Fields.length) {
      return;
    }

    setForm((currentForm) => {
      const fields = [...currentForm.Fields];
      [fields[index], fields[destination]] = [
        fields[destination],
        fields[index],
      ];
      return { ...currentForm, Fields: fields };
    });
  };

  const validateEditor = () => {
    if (!form.Name.trim()) {
      return "Enter a form name.";
    }

    if (!form.Fields.length) {
      return "Add at least one field.";
    }

    const keys = new Set();
    for (const field of form.Fields) {
      if (!field.Label.trim() || !field.FieldKey.trim()) {
        return "Every field needs a label and field key.";
      }

      if (!/^[A-Za-z0-9_-]+$/.test(field.FieldKey)) {
        return `Field key "${field.FieldKey}" can only contain letters, numbers, dashes, and underscores.`;
      }

      if (keys.has(field.FieldKey)) {
        return `Field key "${field.FieldKey}" is duplicated.`;
      }
      keys.add(field.FieldKey);

      if (
        OPTION_FIELD_TYPES.includes(field.FieldType) &&
        !field.OptionsText.split(",").some((option) => option.trim())
      ) {
        return `Add at least one option for "${field.Label}".`;
      }
    }

    return "";
  };

  const buildPayload = () => ({
    Name: form.Name.trim(),
    Description: form.Description.trim() || null,
    ...(form.Slug.trim() ? { Slug: form.Slug.trim() } : {}),
    AccessType: form.AccessType,
    Status: form.Status,
    Fields: form.Fields.map((field, index) => {
      const settings = OPTION_FIELD_TYPES.includes(field.FieldType)
        ? {
            options: field.OptionsText.split(",")
              .map((option) => option.trim())
              .filter(Boolean),
          }
        : {};

      return {
        FieldKey: field.FieldKey.trim(),
        Label: field.Label.trim(),
        FieldType: field.FieldType,
        Placeholder: field.Placeholder.trim() || null,
        HelpText: field.HelpText.trim() || null,
        IsRequired:
          field.FieldType === "heading" ? false : Boolean(field.IsRequired),
        DisplayOrder: index,
        Settings: settings,
      };
    }),
  });

  const handleSave = async () => {
    setError("");
    setMessage("");
    const validationMessage = validateEditor();

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);
      const response = selectedFormId
        ? await api.updateForm(selectedFormId, buildPayload(), token)
        : await api.createForm(buildPayload(), token);
      const savedForm = response?.data?.form;

      if (savedForm) {
        setForm(normalizeForm(savedForm));
        setSelectedFormId(savedForm.Id);
        await loadForms(savedForm.Id);
      }

      setMessage(
        selectedFormId
          ? "Form updated successfully."
          : "Form created successfully.",
      );
    } catch (saveError) {
      setError(extractError(saveError, "Unable to save the form."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFormId) {
      return;
    }

    try {
      setDeleting(true);
      setError("");
      await api.deleteForm(selectedFormId, token);
      setDeleteDialogOpen(false);
      setSelectedFormId("");
      setForm(emptyForm());
      setSubmissions([]);
      setMessage("Form deleted successfully.");
      await loadForms("");
    } catch (deleteError) {
      setError(extractError(deleteError, "Unable to delete the form."));
    } finally {
      setDeleting(false);
    }
  };

  const copyPublicLink = async () => {
    if (!publicUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      setMessage("Form link copied to clipboard.");
    } catch {
      setError("Unable to copy the link. Please copy it from the address field.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fbff", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Chip
                label="Admin Form Builder"
                sx={{
                  mb: 1.5,
                  bgcolor: "rgba(29, 78, 216, 0.1)",
                  color: "#1d4ed8",
                  fontWeight: 700,
                }}
              />
              <Typography variant="h4" fontWeight={800} color="#0f172a">
                Create forms and review responses
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Build reusable forms, choose who can access them, and inspect
                submitted answers in one workspace.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewForm}>
              New form
            </Button>
          </Stack>

          {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
          {message ? (
            <Alert severity="success" onClose={() => setMessage("")}>
              {message}
            </Alert>
          ) : null}

          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4, lg: 3 }}>
              <Paper
                elevation={0}
                sx={{ p: 2.5, borderRadius: 4, border: "1px solid #e2e8f0" }}
              >
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Your forms
                  </Typography>
                  <TextField
                    size="small"
                    label="Search forms"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />

                  {loadingForms ? (
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : filteredForms.length ? (
                    <Stack spacing={1.25}>
                      {filteredForms.map((item) => {
                        const selected =
                          String(item.Id) === String(selectedFormId);
                        return (
                          <Card
                            key={item.Id}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: selected
                                ? "2px solid #2563eb"
                                : "1px solid #e2e8f0",
                              bgcolor: selected ? "#eff6ff" : "white",
                            }}
                          >
                            <CardActionArea
                              onClick={() => {
                                setSelectedFormId(item.Id);
                                setActiveTab(0);
                                setMessage("");
                              }}
                            >
                              <CardContent>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                  <Typography fontWeight={750} noWrap>
                                    {item.Name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={item.Status}
                                    color={statusColor(item.Status)}
                                  />
                                </Stack>
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.75 }}>
                                  /{item.Slug}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.AccessType === "anyone" ? "Anyone" : "Authenticated users"}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
                      <FormIcon sx={{ fontSize: 42, opacity: 0.45 }} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        No forms found.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 8, lg: 9 }}>
              <Paper
                elevation={0}
                sx={{ borderRadius: 4, border: "1px solid #e2e8f0", overflow: "hidden" }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => setActiveTab(value)}
                  sx={{ px: 2, borderBottom: "1px solid #e2e8f0" }}
                >
                  <Tab icon={<FormIcon />} iconPosition="start" label="Form builder" />
                  <Tab
                    icon={<ResponsesIcon />}
                    iconPosition="start"
                    label="Responses"
                    disabled={!selectedFormId}
                  />
                </Tabs>

                {activeTab === 0 ? (
                  <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                    {loadingForm ? (
                      <Box sx={{ py: 10, textAlign: "center" }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Stack spacing={3}>
                        <Grid container spacing={2.5}>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <TextField
                              fullWidth
                              required
                              label="Form name"
                              value={form.Name}
                              onChange={(event) => updateFormValue("Name", event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              select
                              label="Status"
                              value={form.Status}
                              onChange={(event) => updateFormValue("Status", event.target.value)}
                            >
                              <MenuItem value="draft">Draft</MenuItem>
                              <MenuItem value="published">Published</MenuItem>
                              <MenuItem value="closed">Closed</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Description"
                              value={form.Description}
                              onChange={(event) => updateFormValue("Description", event.target.value)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              label="URL slug"
                              value={form.Slug}
                              onChange={(event) => updateFormValue("Slug", slugify(event.target.value))}
                              helperText="Lowercase letters, numbers, and dashes only"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              select
                              label="Who can respond?"
                              value={form.AccessType}
                              onChange={(event) => updateFormValue("AccessType", event.target.value)}
                              helperText={
                                form.AccessType === "anyone"
                                  ? "No login is required."
                                  : "Only signed-in alumni can open and submit."
                              }
                            >
                              <MenuItem value="authenticated">Authenticated users</MenuItem>
                              <MenuItem value="anyone">Anyone with the link</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>

                        {selectedFormId && form.Slug ? (
                          <Paper
                            elevation={0}
                            sx={{ p: 2, borderRadius: 3, bgcolor: "#eff6ff", border: "1px solid #bfdbfe" }}
                          >
                            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={1.5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Shareable form link"
                                value={publicUrl}
                                slotProps={{ input: { readOnly: true } }}
                              />
                              <Tooltip title="Copy link">
                                <IconButton onClick={copyPublicLink} color="primary">
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open form">
                                <IconButton
                                  component="a"
                                  href={publicUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  color="primary"
                                >
                                  <OpenIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        ) : null}

                        <Divider />

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" fontWeight={800}>
                              Form fields
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Fields appear in the same order shown below.
                            </Typography>
                          </Box>
                          <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
                            Add field
                          </Button>
                        </Stack>

                        {form.Fields.length ? (
                          <Stack spacing={2}>
                            {form.Fields.map((field, index) => (
                              <Paper
                                key={field.clientId}
                                elevation={0}
                                sx={{ p: 2.5, borderRadius: 3, border: "1px solid #cbd5e1", bgcolor: "#ffffff" }}
                              >
                                <Stack spacing={2}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Chip label={`Field ${index + 1}`} size="small" />
                                    <Stack direction="row" spacing={0.25}>
                                      <Tooltip title="Move up">
                                        <span>
                                          <IconButton size="small" disabled={index === 0} onClick={() => moveField(index, -1)}>
                                            <ArrowUpIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Move down">
                                        <span>
                                          <IconButton size="small" disabled={index === form.Fields.length - 1} onClick={() => moveField(index, 1)}>
                                            <ArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Delete field">
                                        <IconButton size="small" color="error" onClick={() => removeField(field.clientId)}>
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </Stack>

                                  <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 7 }}>
                                      <TextField
                                        fullWidth
                                        required
                                        label={field.FieldType === "heading" ? "Heading" : "Question label"}
                                        value={field.Label}
                                        onChange={(event) => updateField(field.clientId, "Label", event.target.value)}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                      <TextField
                                        fullWidth
                                        select
                                        label="Field type"
                                        value={field.FieldType}
                                        onChange={(event) => updateField(field.clientId, "FieldType", event.target.value)}
                                      >
                                        {FIELD_TYPES.map((type) => (
                                          <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                      <TextField
                                        fullWidth
                                        required
                                        label="Field key"
                                        value={field.FieldKey}
                                        onChange={(event) => updateField(field.clientId, "FieldKey", keyify(event.target.value))}
                                        helperText="Used as the JSON response key"
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 7 }}>
                                      <TextField
                                        fullWidth
                                        label="Help text"
                                        value={field.HelpText}
                                        onChange={(event) => updateField(field.clientId, "HelpText", event.target.value)}
                                      />
                                    </Grid>

                                    {field.FieldType !== "heading" ? (
                                      <Grid size={{ xs: 12, md: 8 }}>
                                        <TextField
                                          fullWidth
                                          label={field.FieldType === "file" ? "URL placeholder" : "Placeholder"}
                                          value={field.Placeholder}
                                          onChange={(event) => updateField(field.clientId, "Placeholder", event.target.value)}
                                        />
                                      </Grid>
                                    ) : null}

                                    {OPTION_FIELD_TYPES.includes(field.FieldType) ? (
                                      <Grid size={{ xs: 12 }}>
                                        <TextField
                                          fullWidth
                                          required
                                          label="Options"
                                          value={field.OptionsText}
                                          onChange={(event) => updateField(field.clientId, "OptionsText", event.target.value)}
                                          helperText="Separate each option with a comma"
                                        />
                                      </Grid>
                                    ) : null}
                                  </Grid>

                                  {field.FieldType !== "heading" ? (
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={field.IsRequired}
                                          onChange={(event) => updateField(field.clientId, "IsRequired", event.target.checked)}
                                        />
                                      }
                                      label="Required field"
                                    />
                                  ) : null}
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{ p: 5, textAlign: "center", borderRadius: 3, border: "1px dashed #94a3b8", bgcolor: "#f8fafc" }}
                          >
                            <Typography fontWeight={700}>No fields added yet</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                              Start with a short answer, choice field, date, or section heading.
                            </Typography>
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
                              Add first field
                            </Button>
                          </Paper>
                        )}

                        <Divider />
                        <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                          <Button
                            color="error"
                            startIcon={<DeleteIcon />}
                            disabled={!selectedFormId}
                            onClick={() => setDeleteDialogOpen(true)}
                          >
                            Delete form
                          </Button>
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            disabled={saving}
                            onClick={handleSave}
                          >
                            {saving ? "Saving..." : selectedFormId ? "Save changes" : "Create form"}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          Responses
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                          {submissions.length} submitted response{submissions.length === 1 ? "" : "s"}
                        </Typography>
                      </Box>

                      {loadingSubmissions ? (
                        <Box sx={{ py: 8, textAlign: "center" }}>
                          <CircularProgress />
                        </Box>
                      ) : submissions.length ? (
                        submissions.map((submission, index) => (
                          <Accordion
                            key={submission.Id}
                            disableGutters
                            elevation={0}
                            sx={{ border: "1px solid #e2e8f0", borderRadius: "12px !important", overflow: "hidden" }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ sm: "center" }}
                                spacing={1}
                                sx={{ width: "100%", pr: 2 }}
                              >
                                <Box>
                                  <Typography fontWeight={750}>
                                    Response #{submissions.length - index}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {submission.SubmittedByName || "Anonymous respondent"}
                                    {submission.SubmittedByEmail ? ` · ${submission.SubmittedByEmail}` : ""}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDateTime(submission.SubmittedAt)}
                                </Typography>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: "#f8fafc", p: 2.5 }}>
                              <Grid container spacing={2}>
                                {(submission.Answers || []).map((answer) => (
                                  <Grid key={answer.FieldKey} size={{ xs: 12, md: 6 }}>
                                    <Paper elevation={0} sx={{ p: 2, height: "100%", border: "1px solid #e2e8f0", borderRadius: 2.5 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.7 }}>
                                        {answer.Label}
                                      </Typography>
                                      <Typography sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {formatAnswer(answer.Value)}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))
                      ) : (
                        <Paper elevation={0} sx={{ py: 8, textAlign: "center", border: "1px dashed #94a3b8", borderRadius: 3 }}>
                          <ResponsesIcon sx={{ fontSize: 48, color: "#94a3b8" }} />
                          <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                            No responses yet
                          </Typography>
                          <Typography color="text.secondary">
                            Responses will appear here after this form is submitted.
                          </Typography>
                        </Paper>
                      )}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this form?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This permanently removes the form and all of its submitted responses.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete form"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FormBuilder;
